// frontend-react/src/api/services.js
import client from "./client";

/**
 * Servicio para la gestión de BlogPosts.
 */
export const blogService = {
  // Obtener posts publicados (Público)
  getPublishedPosts: () => client.get("/blog-posts/published/"),

  // Obtener todos los posts (Requiere autenticación, devuelve según rol)
  getAllPosts: () => client.get("/blog-posts/"),

  // Obtener post por slug
  getPostBySlug: (slug, config = {}) => client.get(`/blog-posts/s/${slug}/`, config),

  // Crear nuevo post
  createPost: (postData) => client.post("/blog-posts/", postData),

  // Actualizar post (Usando PATCH para actualizaciones parciales)
  updatePost: (id, postData) => client.patch(`/blog-posts/${id}/`, postData),

  // Eliminar post
  deletePost: (id) => client.delete(`/blog-posts/${id}/`),

  // Obtener posts por categoría
  getPostsByCategory: (slug) => client.get(`/blog-posts/published/?category=${slug}`),

  // Obtener posts destacados para el carousel
  getFeaturedPosts: () => client.get("/blog-posts/featured/"),

  // Obtener Smart Feed (Featured + Latest sin llamadas externas)
  getSmartFeed: () => client.get("/blog-posts/smart_feed/"),
};

/**
 * Servicio para la gestión de categorías.
 * Implementación de manejo de errores y consistencia de datos.
 */
export const categoryService = {
  // Obtener categorías públicas (MEJORADO con async/await y manejo de errores)
  getCategories: async () => {
    try {
      const response = await client.get("/categories/");

      // 🚨 MANEJO DE PAGINACIÓN: El backend devuelve {count, next, previous, results}
      let categoriesData;
      if (response.data && response.data.results) {
        // Respuesta paginada - usar results
        categoriesData = response.data.results;
      } else if (Array.isArray(response.data)) {
        // Respuesta directa (sin paginación)
        categoriesData = response.data;
      } else {
        categoriesData = [];
      }

      return {
        ...response,
        data: categoriesData,
      };
    } catch (error) {
      console.error("❌ Error fetching categories:", error);
      return { data: [] };
    }
  },

  // Obtener una categoría pública por slug
  getCategory: (slug) => client.get(`/categories/${slug}/`),

  // Nota: updateCategory ha sido movido/consolidado en extendedAdminService.
};

/**
 * Servicio de autenticación.
 */
export const authService = {
  // Autenticación con Google Access Token
  googleAuth: (accessToken) => client.post("/auth/google/", { access_token: accessToken }),

  // Login tradicional
  traditionalLogin: (credentials) => client.post("/auth/login/", credentials),

  // Registro tradicional
  register: (userData) => client.post("/auth/register/", userData),

  // Cambio de contraseña
  changePassword: (data) => client.post("/auth/change-password/", data),

  // Solicitar recuperación de contraseña
  requestPasswordReset: (email) => client.post("/auth/password-reset/", { email }),

  // Confirmar recuperación de contraseña
  confirmPasswordReset: (data) => client.post("/auth/password-reset/confirm/", data),
};

/**
 * Servicio de información de usuario.
 */
export const userService = {
  // Obtener datos del usuario autenticado actual
  getCurrentUser: () => client.get("/users/current/"),
};

// --- SERVICIOS BASE PARA ADMIN Y ESTADÍSTICAS ---

/**
 * Servicios base para tareas administrativas que existían previamente.
 */
const baseAdminService = {
  // Generar contenido de blog usando Gemini (Mantener aquí o mover a extendedAdminService)
  // Lo movemos a extendedAdminService para consolidar todas las llamadas de IA bajo Admin

  // Obtener posts programados (asumiendo filtro por query param)
  getScheduledPosts: async () => {
    try {
      const response = await client.get("/blog-posts/?status=scheduled");
      // Validar que la respuesta sea un array
      return {
        ...response,
        data: Array.isArray(response.data) ? response.data : [],
      };
    } catch (error) {
      console.error("Error in getScheduledPosts:", error);
      // Siempre devolver estructura consistente
      return {
        data: [],
        status: error.response?.status || 500,
        message: error.response?.data?.message || "Error del servidor",
      };
    }
  },

  // Actualizar la programación de un post (usa el mismo endpoint de updatePost)
  updatePostSchedule: (id, data) => client.patch(`/blog-posts/${id}/`, data),

  // Obtener configuraciones de autogeneración (Será reemplazado/complementado en extendedAdminService)
  getAutoConfigs: () => client.get("/auto-configs/"),

  // Actualizar configuración de autogeneración (Será reemplazado/complementado en extendedAdminService)
  updateAutoConfig: (id, data) => client.patch(`/auto-configs/${id}/`, data),

  // =======================================================
  // GESTIÓN DE USUARIOS
  // =======================================================

  // Obtener todos los usuarios (Ruta de gestión de Admin)
  getUsers: async (page = 1) => {
    try {
      const response = await client.get(`/manage-users/?page=${page}`);
      console.log("🔍 Users API Response:", response);

      // 🚨 MANEJO COMPLETO DE PAGINACIÓN
      if (response.data && response.data.results) {
        // Respuesta paginada: {count, next, previous, results}
        return {
          data: response.data.results,
          pagination: {
            count: response.data.count,
            next: response.data.next,
            previous: response.data.previous,
            currentPage: page,
          },
        };
      } else if (Array.isArray(response.data)) {
        // Respuesta directa (sin paginación)
        return {
          data: response.data,
          pagination: null,
        };
      } else {
        // Respuesta inesperada
        console.warn("⚠️ Formato de respuesta inesperado:", response.data);
        return {
          data: [],
          pagination: null,
        };
      }
    } catch (error) {
      console.error("❌ Error in getUsers:", error);
      return {
        data: [],
        pagination: null,
        error: error.response?.data?.message || "Error del servidor",
      };
    }
  },

  // Actualizar rol de usuario a través de la acción personalizada en el backend
  updateUserRole: (userId, role) =>
    client.patch(`/manage-users/${userId}/update_role/`, { role }),

  // Eliminar usuario
  deleteUser: (userId) => client.delete(`/manage-users/${userId}/`),

  // Obtener estadísticas de usuarios
  getUserStats: () => client.get("/user-stats/"),
};

// --- NUEVOS SERVICIOS EXTENDIDOS (ACTUALIZADO) ---

/**
 * Servicios extendidos para gestión completa y lógica de IA (NUEVO) 🚀
 * **Incluye:** Gestión completa de Categorías, Configuración de Autogeneración y Generación de Contenido.
 */
export const extendedAdminService = {
  // Generación de Contenido IA
  generateBlog: (data) => client.post("/generate-blog/", data), // Movido de iaGenerationService
  saveGeneratedPost: (data) => client.post("/blog-posts/", data), // Mantenido/movido de iaGenerationService

  // Gestión de categorías - IMPLEMENTACIÓN COMPLETA
  createCategory: (data) => client.post("/manage-categories/", data),
  updateCategory: (id, data) => client.patch(`/manage-categories/${id}/`, data),
  deleteCategory: (id) => client.delete(`/manage-categories/${id}/`),
  createDefaultCategory: () => client.post("/create-default-category/"),

  // Generación de categorías virales (AGREGADO EXPLICITAMENTE AQUÍ)
  generateViralCategories: () => client.post("/generate-viral-categories/"),

  // Configuración de auto-generación
  createAutoConfig: (data) => client.post("/auto-configs/", data),
  deleteAutoConfig: (id) => client.delete(`/auto-configs/${id}/`),
  executeAutoGeneration: (id) => client.post(`/auto-configs/${id}/execute/`),
  
  // Nuevos servicios de control de auto-generación
  resetLastExecution: (id) => client.post(`/auto-configs/${id}/reset_last_execution/`),
  forceExecute: (id) => client.post(`/auto-configs/${id}/force_execute/`),
  getExecutionStatus: (id) => client.get(`/auto-configs/${id}/execution_status/`),
  getCeleryStatus: () => client.get('/auto-configs/celery_status/'),
  getExecutionLogs: (id) => client.get(`/auto-configs/${id}/execution_logs/`),
  
  generateViralCategories: (data) =>
    client.post("/generate-viral-categories/", data),
    
  // Gestión de Posts (CRUD Admin)
  getPost: (id, config = {}) => client.get(`/blog-posts/${id}/`, config),
  updatePost: (id, data) => client.patch(`/blog-posts/${id}/`, data),
};

/**
 * CONSOLIDACIÓN: adminService = baseAdminService + extendedAdminService
 * (Exportación final y principal del servicio de Admin, eliminando iaGenerationService)
 */
export const adminService = {
  ...baseAdminService, // Servicios base de Admin (Users, Scheduled Posts, etc.)
  ...extendedAdminService, // Servicios extendidos (Category Management, IA Generation)
};

/**
 * Servicios de estadísticas del blog.
 * Requiere permisos de Admin (usando ReadOnlyModelViewSet en el backend).
 */
export const statsService = {
  getStats: async () => {
    try {
      const response = await client.get("/blog-stats/");
      // Asegurar que siempre devolvemos un array
      return {
        ...response,
        data: Array.isArray(response.data) ? response.data : [],
      };
    } catch (error) {
      console.error("Error fetching stats:", error);
      return { data: [] }; // Devolver array vacío en caso de error
    }
  },

  getPostStats: async (postId) => {
    try {
      // CORRECCIÓN: Validar que postId existe antes de hacer la llamada
      if (!postId) {
        console.warn("getPostStats llamado sin postId");
        return { data: null };
      }
      const response = await client.get(`/blog-stats/${postId}/`);
      return response;
    } catch (error) {
      console.error("Error fetching post stats:", error);
      return { data: null };
    }
  },
};

export const analyticsService = {
  trackView: (slug) => client.post('/analytics/track/', { slug }),
  getAnalyticsDashboard: () => client.get('/analytics/dashboard/'),
};

/**
 * Servicio para noticias externas de GNews API.
 * Proporciona noticias trending de Venezuela/LATAM con caché inteligente.
 */
export const gnewsService = {
  // Obtener noticias trending (Venezuela por defecto)
  getTrendingNews: (country = 've', limit = 10) => 
    client.get(`/external-news/trending/?country=${country}&limit=${limit}`),
  
  // Obtener noticias por categoría
  getNewsByCategory: (category = 'venezuela_tech', forceRefresh = false) =>
    client.get(`/external-news/by_category/?category=${category}&force_refresh=${forceRefresh}`),
  
  // Obtener todas las noticias con filtros
  getAllNews: (params = {}) => client.get('/external-news/', { params }),
  
  // Obtener estadísticas de uso de API (solo admin)
  getAPIUsage: () => client.get('/external-news/api_usage/'),
  
  getArticleById: (id) => client.get(`/external-news/${id}/`),
  
  // Forzar actualización de noticias (solo admin)
  refreshNews: (category = 'venezuela_tech') =>
    client.post('/external-news/refresh_news/', { category }),
};
